import { useEffect, useState } from "react";
import IFBanner from "../components/IF_Template/IF_banner";
import { motion } from "framer-motion";
import { getTeam, getTeamCache } from "../services";
import IFSubMenu from "../components/IF_SubMenu";
import IFHeading from "../components/IF_Heading";

function getCoachDetails(label) {
    const l = (label || "").trim().toLowerCase();
    if (l.includes("specialist") || l === "certified") {
        return {
            category: "specialist",
            badgeText: "I-FIT CERTIFIED SPECIALIST",
            badgeBorder: "border-ungu/50 group-hover:border-pink",
            badgeGradient: "group-hover:from-ungu group-hover:to-pink",
            dotColor: "bg-pink",
            codeTheme: "border-ungu/40 text-pink bg-[#08090d]/90 group-hover:border-pink group-hover:text-white",
            auraGradient: "bg-[radial-gradient(ellipse_at_50%_90%,rgba(106,16,107,0.45)_0%,rgba(234,42,94,0.25)_45%,transparent_75%)]",
            ringBorder: "border-ungu/40 group-hover:border-pink",
        };
    }
    if (l.includes("wanita") || l.includes("woman") || l.includes("female")) {
        return {
            category: "woman",
            badgeText: "I-FIT WOMAN CERTIFIED COACH",
            badgeBorder: "border-pink/50 group-hover:border-pink",
            badgeGradient: "group-hover:from-pink group-hover:to-ungu",
            dotColor: "bg-pink",
            codeTheme: "border-pink/40 text-pink bg-[#08090d]/90 group-hover:border-pink group-hover:text-white",
            auraGradient: "bg-[radial-gradient(ellipse_at_50%_90%,rgba(234,42,94,0.45)_0%,rgba(106,16,107,0.25)_45%,transparent_75%)]",
            ringBorder: "border-pink/40 group-hover:border-pink",
        };
    }
    if (l.includes("pria") || l.includes("man") || l.includes("male")) {
        return {
            category: "man",
            badgeText: "I-FIT MAN CERTIFIED COACH",
            badgeBorder: "border-biruMuda/50 group-hover:border-biruMuda",
            badgeGradient: "group-hover:from-biruMuda group-hover:to-ungu",
            dotColor: "bg-biruMuda",
            codeTheme: "border-biruMuda/40 text-biruMuda bg-[#08090d]/90 group-hover:border-biruMuda group-hover:text-white",
            auraGradient: "bg-[radial-gradient(ellipse_at_50%_90%,rgba(11,180,192,0.35)_0%,rgba(234,42,94,0.2)_45%,transparent_75%)]",
            ringBorder: "border-biruMuda/40 group-hover:border-biruMuda",
        };
    }
    return {
        category: "other",
        badgeText: label ? `I-FIT CERTIFIED ${label.toUpperCase()} COACH` : "I-FIT CERTIFIED COACH",
        badgeBorder: "border-white/15 group-hover:border-pink",
        badgeGradient: "group-hover:from-pink group-hover:to-ungu",
        dotColor: "bg-biruMuda",
        codeTheme: "border-white/20 text-gray-200 bg-[#08090d]/90 group-hover:border-pink group-hover:text-pink",
        auraGradient: "bg-[radial-gradient(ellipse_at_50%_90%,rgba(234,42,94,0.35)_0%,rgba(11,180,192,0.15)_45%,transparent_75%)]",
        ringBorder: "border-biruMuda/30 group-hover:border-pink",
    };
}

function SummonCoachCard({ coach, index }) {
    const details = getCoachDetails(coach.label);

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: (index % 8) * 0.08, ease: "linear" }}
            className="h-full relative"
        >
            <div className="relative rounded-3xl bg-[#0c0e16] border border-white/10 group-hover:bg-transparent group-hover:border-transparent transition-all duration-500 group cursor-pointer h-full flex flex-col justify-between overflow-hidden">
                {/* Main Character Summoning Chamber */}
                <div className="w-full aspect-[3/4] relative flex items-end justify-center">
                    {/* Top Coach ID Badge (Displayed on image) */}
                    {coach.code && (
                        <div className="absolute top-3.5 left-3.5 z-30 pointer-events-none">
                            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full border backdrop-blur-md shadow-lg transition-all duration-300 ${details.codeTheme}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${details.dotColor} animate-pulse`} />
                                <span className="text-[10px] sm:text-[11px] font-mono font-black tracking-wider uppercase">
                                    {coach.code}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Vertical Summoning Light Aura (Shoots up from portal on hover) */}
                    <div className={`absolute inset-0 ${details.auraGradient} opacity-0 group-hover:opacity-100 scale-y-75 group-hover:scale-y-100 transition-all duration-500 origin-bottom pointer-events-none z-0`} />

                    {/* Summoning Rune Platform / Perspective Circle at the Base */}
                    <div className="absolute bottom-6 inset-x-0 h-14 pointer-events-none z-10 flex items-center justify-center">
                        {/* Outer Perspective Energy Ring */}
                        <div
                            className={`w-[85%] h-full rounded-full border ${details.ringBorder} group-hover:scale-115 transition-all duration-500`}
                            style={{
                                transform: "perspective(300px) rotateX(72deg)",
                                background: "radial-gradient(ellipse, rgba(234,42,94,0.32) 0%, rgba(11,180,192,0.15) 50%, transparent 75%)",
                            }}
                        />
                        {/* Inner Core Flare */}
                        <div
                            className="absolute w-1/2 h-1/2 rounded-full bg-[radial-gradient(ellipse,rgba(11,180,192,0.35)_0%,transparent_70%)] group-hover:bg-[radial-gradient(ellipse,rgba(234,42,94,0.7)_0%,transparent_70%)] transition-all duration-500 opacity-40 group-hover:opacity-100"
                            style={{ transform: "perspective(300px) rotateX(72deg)" }}
                        />
                    </div>

                    {/* Summoned Character Body (Rises & illuminates upon summon, breaking out of card) */}
                    <div className="relative z-15 w-full h-full flex items-end justify-center px-3 pt-4">
                        <img
                            alt="I-FIT Coach"
                            src={process.env.REACT_APP_IMAGE_ALBUM_URL + coach.filename}
                            loading="lazy"
                            className="w-full h-full object-contain object-bottom transition-all duration-500 ease-out transform translate-y-3 group-hover:translate-y-[-18px] scale-100 group-hover:scale-115 brightness-[0.75] contrast-[1.05] opacity-80 group-hover:brightness-[1.12] group-hover:contrast-[1.08] group-hover:opacity-100 select-none pointer-events-none"
                            style={{
                                maskImage: "linear-gradient(to bottom, black 82%, transparent 100%)",
                                WebkitMaskImage: "linear-gradient(to bottom, black 82%, transparent 100%)",
                            }}
                        />
                    </div>

                    {/* Bottom Ground Ambient Vignette (Fades away on hover to release character) */}
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0c0e16] via-[#0c0e16]/60 to-transparent pointer-events-none z-20 group-hover:opacity-0 transition-opacity duration-500" />

                    {/* Elevated Status Badge */}
                    <div className="absolute bottom-3 inset-x-3 text-center z-30 transition-transform duration-500 transform group-hover:translate-y-[-6px]">
                        <div className={`px-3 py-1.5 rounded-full bg-[#08090d]/85 border ${details.badgeBorder} group-hover:bg-gradient-to-r ${details.badgeGradient} transition-all duration-500 inline-flex items-center gap-2 shadow-lg`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${details.dotColor} group-hover:bg-white animate-pulse shrink-0`} />
                            <span className="text-[9.5px] sm:text-[10px] md:text-[10.5px] font-black tracking-wider uppercase text-gray-200 group-hover:text-white transition-colors duration-300 truncate max-w-[210px]">
                                {details.badgeText}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default function Team() {
    const cached = getTeamCache();
    const [data, setDatas] = useState(() => cached || { datas: [], album: [] });
    const [load, setLoad] = useState(() => !cached || !cached.datas || cached.datas.length === 0);
    const [activeTab, setActiveTab] = useState("all");

    useEffect(() => {
        getTeam().then((res) => {
            if (res && res.datas && res.datas.length > 0) {
                setDatas(res);
            }
            setLoad(false);
        });
    }, []);

    if (load || !data.datas || data.datas.length === 0) {
        return (
            <div className="min-h-[70vh] w-full flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-white/20 border-t-pink animate-spin" />
            </div>
        );
    }

    // Sort coaches by sort_order ASC, id ASC
    const sortedCoaches = [...(data.album || [])].sort((a, b) => {
        const orderA = a.sort_order !== undefined && a.sort_order !== null ? Number(a.sort_order) : 999;
        const orderB = b.sort_order !== undefined && b.sort_order !== null ? Number(b.sort_order) : 999;
        if (orderA !== orderB) return orderA - orderB;
        return (Number(a.id) || 0) - (Number(b.id) || 0);
    });

    // Division 1: Specialist
    const specialistCoaches = sortedCoaches.filter((c) => {
        const l = (c.label || "").toLowerCase();
        return l.includes("specialist");
    });

    // Division 2: Woman Coach
    const womanCoaches = sortedCoaches.filter((c) => {
        const l = (c.label || "").toLowerCase();
        return l.includes("wanita") || l.includes("woman") || l.includes("female");
    });

    // Division 3: Man Coach
    const manCoaches = sortedCoaches.filter((c) => {
        const l = (c.label || "").toLowerCase();
        return l.includes("pria") || l.includes("man") || l.includes("male");
    });

    // Fallback: Coaches without division
    const uncategorizedCoaches = sortedCoaches.filter((c) => {
        const l = (c.label || "").toLowerCase();
        return (
            !l.includes("specialist") &&
            !l.includes("wanita") &&
            !l.includes("woman") &&
            !l.includes("female") &&
            !l.includes("pria") &&
            !l.includes("man") &&
            !l.includes("male")
        );
    });

    // Structure sections
    const sections = [
        {
            id: "specialist",
            title: "I-FIT CERTIFIED SPECIALIST COACH",
            shortTitle: "Specialist",
            subtitle: "Master Trainers & Elite Movement Specialists",
            dotColor: "bg-pink",
            badgeBorder: "border-ungu/40 text-pink bg-ungu/15",
            items: specialistCoaches,
        },
        {
            id: "woman",
            title: "I-FIT WOMAN CERTIFIED COACH",
            shortTitle: "Woman Coach",
            subtitle: "Dedicated Female Personal Trainers & Posture Transformation Mentors",
            dotColor: "bg-pink",
            badgeBorder: "border-pink/40 text-pink bg-pink/15",
            items: womanCoaches,
        },
        {
            id: "man",
            title: "I-FIT MAN CERTIFIED COACH",
            shortTitle: "Man Coach",
            subtitle: "High-Performance Strength, Conditioning & Biomechanics Coaches",
            dotColor: "bg-biruMuda",
            badgeBorder: "border-biruMuda/40 text-biruMuda bg-biruMuda/15",
            items: manCoaches,
        },
    ];

    if (uncategorizedCoaches.length > 0) {
        sections.push({
            id: "other",
            title: "I-FIT CERTIFIED COACHES",
            shortTitle: "Coaches",
            subtitle: "Professional Certified Fitness & Posture Trainers",
            dotColor: "bg-biruMuda",
            badgeBorder: "border-white/20 text-gray-300 bg-white/10",
            items: uncategorizedCoaches,
        });
    }

    // Filter active sections based on active tab
    const visibleSections = sections.filter((sec) => {
        if (sec.items.length === 0) return false;
        if (activeTab === "all") return true;
        return sec.id === activeTab;
    });

    return (
        <div className="flex flex-col w-full overflow-x-hidden">
            <IFBanner>
                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "linear" }}
                >
                    <div className="max-w-[600px]">
                        <IFSubMenu
                            as="h1"
                            text1={data.datas[0]?.value}
                            text2={data.datas[1]?.value}
                        />
                        <p className="text-xs sm:text-sm md:text-base text-gray-300 leading-relaxed">
                            Tim pelatih bersertifikasi profesional I-FIT Indonesia berdedikasi membimbing dan mendampingi proses perbaikan postur tubuh serta peningkatan level kebugaran Anda dengan standar internasional.
                        </p>
                    </div>
                </motion.div>
            </IFBanner>

            <section className="py-12 md:py-20 px-4 md:px-8">
                <IFHeading>OUR PROFESSIONAL COACHES</IFHeading>

                {/* Division Filter Tabs */}
                <div className="max-w-[1300px] mx-auto mt-6 mb-12 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                    <button
                        type="button"
                        onClick={() => setActiveTab("all")}
                        className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-black tracking-wider uppercase transition-all duration-300 cursor-pointer ${
                            activeTab === "all"
                                ? "bg-gradient-to-r from-pink to-ungu text-white shadow-lg shadow-pink/20 scale-105"
                                : "bg-[#12141e] text-gray-400 border border-white/10 hover:text-white hover:border-pink/40"
                        }`}
                    >
                        ALL DIVISIONS ({sortedCoaches.length})
                    </button>

                    {sections
                        .filter((sec) => sec.items.length > 0)
                        .map((sec) => (
                            <button
                                key={sec.id}
                                type="button"
                                onClick={() => setActiveTab(sec.id)}
                                className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-black tracking-wider uppercase transition-all duration-300 cursor-pointer ${
                                    activeTab === sec.id
                                        ? "bg-gradient-to-r from-pink to-ungu text-white shadow-lg shadow-pink/20 scale-105"
                                        : "bg-[#12141e] text-gray-400 border border-white/10 hover:text-white hover:border-pink/40"
                                }`}
                            >
                                {sec.shortTitle} ({sec.items.length})
                            </button>
                        ))}
                </div>

                {/* Division Sections */}
                <div className="max-w-[1300px] mx-auto flex flex-col gap-16 md:gap-20">
                    {visibleSections.length > 0 ? (
                        visibleSections.map((sec) => (
                            <div key={sec.id} className="flex flex-col">
                                {/* Section Header Divider */}
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-4 border-b border-white/10 mb-8">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-3">
                                            <span className={`w-2.5 h-2.5 rounded-full ${sec.dotColor} animate-pulse`} />
                                            <h2 className="text-lg sm:text-xl md:text-2xl font-black tracking-wider uppercase text-white">
                                                {sec.title}
                                            </h2>
                                            <span className={`px-3 py-0.5 rounded-full text-xs font-bold border ${sec.badgeBorder}`}>
                                                {sec.items.length} {sec.items.length === 1 ? "Coach" : "Coaches"}
                                            </span>
                                        </div>
                                        <p className="text-xs sm:text-sm text-gray-400 mt-1 pl-5.5">
                                            {sec.subtitle}
                                        </p>
                                    </div>
                                </div>

                                {/* Coach Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {sec.items.map((coach, index) => (
                                        <SummonCoachCard key={coach.id || index} coach={coach} index={index} />
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        /* Fallback if no categorized coaches */
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                            {sortedCoaches.map((coach, index) => (
                                <SummonCoachCard key={coach.id || index} coach={coach} index={index} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
